import React, { useState, useEffect } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Card, CardContent } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { HelpCircle, BookOpen, X, ExternalLink, Bug, Loader2 } from 'lucide-react';
import BugReportModal from './BugReportModal';
import bugReportCapture from '@/services/bugReportCapture';
import { usePermissions } from '@/contexts/PermissionContext';
import { setCopilotPageContext } from '@/utils/copilotPageContext';

const API = process.env.REACT_APP_BACKEND_URL || '';

const ContextualHelpWidget = ({ triggerOpen, onOpenChange }) => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { hasPermission } = usePermissions();
  const platformCasePerm = 'help.platform_case.create';
  const canReportPlatformIssue = hasPermission(platformCasePerm)
    || hasPermission('copilot.company.use');
  const [open, setOpen] = useState(false);
  const [helpData, setHelpData] = useState({ articles: [], tooltips: [] });
  const [loading, setLoading] = useState(false);
  const [bugReportOpen, setBugReportOpen] = useState(false);
  const [preCapturedData, setPreCapturedData] = useState(null);
  const [capturing, setCapturing] = useState(false);
  const [intake, setIntake] = useState({ ai_help_enabled: true, suggested_articles: [] });
  const [coSubject, setCoSubject] = useState('');
  const [coBody, setCoBody] = useState('');
  const [coCategory, setCoCategory] = useState('general');
  const [coSubmitting, setCoSubmitting] = useState(false);
  const [prefillConfirmed, setPrefillConfirmed] = useState(false);

  useEffect(() => {
    const ps = searchParams.get('help_subject');
    const pd = searchParams.get('help_description');
    if (ps || pd) {
      setCoSubject(ps || '');
      setCoBody(pd || '');
      setPrefillConfirmed(false);
      setOpen(true);
    }
  }, [searchParams]);

  // Support external open trigger
  useEffect(() => {
    if (triggerOpen) {
      setOpen(true);
      onOpenChange?.(false);
    }
  }, [triggerOpen, onOpenChange]);

  useEffect(() => {
    if (open) {
      setCopilotPageContext({ mode: 'help', route: location.pathname, channel: 'web' });
      fetchContextualHelp();
      axios.get(`${API}/api/help-support/intake`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
      }).then((res) => {
        setIntake(res.data);
        const cats = res.data.categories || [];
        if (cats.length && !cats.some((c) => c.key === coCategory)) {
          setCoCategory(cats[0].key);
        }
      }).catch(() => setIntake({ ai_help_enabled: true, suggested_articles: [] }));
    }
  }, [open, location.pathname]);

  const fetchContextualHelp = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      if (!token) return;

      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/api/help/contextual?route=${encodeURIComponent(location.pathname)}`, { headers });
      setHelpData({
        articles: res.data.articles || [],
        tooltips: res.data.tooltips || []
      });
    } catch (error) {
      console.error('Failed to fetch contextual help:', error);
    } finally {
      setLoading(false);
    }
  };

  // Capture screenshot BEFORE opening the bug report modal
  const handleReportBugClick = async () => {
    setCapturing(true);
    
    // First close the help panel so screenshot is clean
    setOpen(false);
    
    // Wait for panel to close
    await new Promise(resolve => setTimeout(resolve, 300));
    
    try {
      // Capture the page data while help panel is closed
      const data = await bugReportCapture.compileReportData();
      setPreCapturedData(data);
      
      // Now open the bug report modal with pre-captured data
      setBugReportOpen(true);
    } catch (error) {
      console.error('Failed to capture bug report data:', error);
      // Still open modal, it will capture fresh data
      setBugReportOpen(true);
    } finally {
      setCapturing(false);
    }
  };

  // Clear pre-captured data when modal closes
  const handleBugReportClose = (isOpen) => {
    setBugReportOpen(isOpen);
    if (!isOpen) {
      setPreCapturedData(null);
    }
  };

  const submitCompanyCase = async () => {
    if (!coSubject.trim() || !coBody.trim()) return;
    const ps = searchParams.get('help_subject');
    const pd = searchParams.get('help_description');
    if ((ps || pd) && !prefillConfirmed) {
      setPrefillConfirmed(true);
      return;
    }
    setCoSubmitting(true);
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(`${API}/api/help-support/company-cases`, {
        subject: coSubject.trim(),
        description: coBody.trim(),
        ...(intake.ai_help_enabled ? {} : { category: coCategory }),
      }, { headers: { Authorization: `Bearer ${token}` } });
      setCoSubject('');
      setCoBody('');
      setOpen(false);
      window.alert('Your support request was submitted. View it under My support requests.');
    } catch (e) {
      window.alert(e.response?.data?.detail || 'Could not submit request');
    } finally {
      setCoSubmitting(false);
    }
  };

  return (
    <>
      {/* Help Sheet */}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent className="w-full sm:max-w-md" data-help-sheet="true">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <BookOpen className="h-5 w-5" />
              Help & Resources
            </SheetTitle>
            <SheetDescription>
              Find helpful articles and tips for this page
            </SheetDescription>
          </SheetHeader>

          <ScrollArea className="h-[calc(100vh-120px)] mt-6">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : (
              <div className="space-y-6">
                {!intake.ai_help_enabled && (
                  <div className="space-y-3 rounded-lg border p-4 bg-muted/30">
                    <p className="text-sm">{intake.welcome_copy || 'Submit a request and our team will respond.'}</p>
                    {(intake.suggested_articles || []).length > 0 && (
                      <div className="space-y-2">
                        <p className="text-xs font-medium text-muted-foreground">Suggested articles</p>
                        {(intake.suggested_articles || []).map((a) => (
                          <a
                            key={a.slug || a.url}
                            href={a.url || `/help/articles/${a.slug}`}
                            className="block text-sm text-primary underline"
                          >
                            {a.title}
                          </a>
                        ))}
                      </div>
                    )}
                    <input
                      className="w-full border rounded-md px-3 py-2 text-sm"
                      placeholder="Subject"
                      value={coSubject}
                      onChange={(e) => setCoSubject(e.target.value)}
                    />
                    <textarea
                      className="w-full border rounded-md px-3 py-2 text-sm min-h-[80px]"
                      placeholder="Describe your issue"
                      value={coBody}
                      onChange={(e) => setCoBody(e.target.value)}
                    />
                    {!intake.ai_help_enabled && (intake.categories || []).length > 0 && (
                      <div className="space-y-1">
                        <Label className="text-xs">Category</Label>
                        <Select value={coCategory} onValueChange={setCoCategory}>
                          <SelectTrigger className="h-9"><SelectValue placeholder="Choose category" /></SelectTrigger>
                          <SelectContent>
                            {(intake.categories || []).filter((c) => c.key?.trim()).map((c) => (
                              <SelectItem key={c.key} value={c.key}>{c.label || c.key}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    <Button className="w-full" onClick={submitCompanyCase} disabled={coSubmitting}>
                      {coSubmitting ? 'Submitting…' : prefillConfirmed || !(searchParams.get('help_subject') || searchParams.get('help_description'))
                        ? 'Submit company support request'
                        : 'Confirm and submit request'}
                    </Button>
                    <Button variant="link" className="w-full" asChild>
                      <a href="/help/my-requests">My support requests</a>
                    </Button>
                  </div>
                )}
                {/* Relevant Articles */}
                {helpData.articles.length > 0 && (
                  <div>
                    <h3 className="font-semibold mb-3 flex items-center gap-2">
                      <BookOpen className="h-4 w-4" />
                      Helpful Articles
                    </h3>
                    <div className="space-y-3">
                      {helpData.articles.map(article => (
                        <Card key={article.id} className="hover:shadow-md transition-shadow">
                          <CardContent className="p-4">
                            <a 
                              href={`/help/articles/${article.slug}`} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="block"
                            >
                              <div className="flex items-start justify-between gap-2">
                                <div className="flex-1">
                                  <h4 className="font-medium text-sm mb-1">{article.title}</h4>
                                  {article.excerpt && (
                                    <p className="text-xs text-muted-foreground line-clamp-2">{article.excerpt}</p>
                                  )}
                                </div>
                                <ExternalLink className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                              </div>
                            </a>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}

                {/* UI Tooltips */}
                {helpData.tooltips.length > 0 && (
                  <div>
                    <h3 className="font-semibold mb-3 flex items-center gap-2">
                      <HelpCircle className="h-4 w-4" />
                      Quick Tips
                    </h3>
                    <div className="space-y-3">
                      {helpData.tooltips.map(tooltip => (
                        <Card key={tooltip.id} className="bg-muted/50">
                          <CardContent className="p-4">
                            <h4 className="font-medium text-sm mb-1">{tooltip.title}</h4>
                            <p className="text-xs text-muted-foreground">{tooltip.content}</p>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}

                {/* No Help Available */}
                {helpData.articles.length === 0 && helpData.tooltips.length === 0 && (
                  <div className="text-center py-12 text-muted-foreground">
                    <HelpCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p className="mb-2">No specific help articles for this page yet.</p>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => window.open('/help', '_blank')}
                    >
                      Browse All Help Articles
                    </Button>
                  </div>
                )}

                {/* General Help Link */}
                <div className="pt-4 border-t space-y-2">
                  <Button 
                    variant="outline" 
                    className="w-full"
                    onClick={() => window.open('/help', '_blank')}
                  >
                    <BookOpen className="h-4 w-4 mr-2" />
                    Visit Help Center
                  </Button>
                  
                  {canReportPlatformIssue && (
                    <Button
                      variant="destructive"
                      className="w-full"
                      onClick={handleReportBugClick}
                      disabled={capturing}
                    >
                      {capturing ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Bug className="h-4 w-4 mr-2" />
                      )}
                      {capturing ? 'Capturing...' : 'Report a Bug'}
                    </Button>
                  )}
                </div>
              </div>
            )}
          </ScrollArea>
        </SheetContent>
      </Sheet>

      {/* Bug Report Modal */}
      <BugReportModal 
        open={bugReportOpen} 
        onOpenChange={handleBugReportClose}
        preCapturedData={preCapturedData}
      />
    </>
  );
};

export default ContextualHelpWidget;
